import { create } from 'zustand';

import type {
  LocalRecoveryProgress,
  LocalRecoverySummary,
  UploadPreparationJob,
} from '../types/uploadPreparation';

interface UploadPreparationState {
  job?: UploadPreparationJob;
  begin: (progress: LocalRecoveryProgress) => number;
  update: (id: number, progress: LocalRecoveryProgress) => void;
  complete: (id: number, summary: LocalRecoverySummary) => void;
  fail: (id: number) => void;
  requestStop: (id: number) => void;
  dismiss: () => void;
}

let nextJobId = 0;

export const useUploadPreparationStore = create<UploadPreparationState>((set) => ({
  job: undefined,
  begin: (progress) => {
    const id = ++nextJobId;
    set({ job: { id, status: 'running', progress } });
    return id;
  },
  update: (id, progress) =>
    set((state) =>
      state.job?.id === id && state.job.status === 'running'
        ? { job: { ...state.job, progress } }
        : state,
    ),
  complete: (id, summary) =>
    set((state) => (state.job?.id === id ? { job: { id, status: 'complete', summary } } : state)),
  fail: (id) => set((state) => (state.job?.id === id ? { job: { id, status: 'failed' } } : state)),
  requestStop: (id) =>
    set((state) =>
      state.job?.id === id && state.job.status === 'running'
        ? { job: { ...state.job, stopRequested: true } }
        : state,
    ),
  dismiss: () => set({ job: undefined }),
}));

export const uploadPreparationActions = {
  get: (): UploadPreparationJob | undefined => useUploadPreparationStore.getState().job,
  begin: (progress: LocalRecoveryProgress): number =>
    useUploadPreparationStore.getState().begin(progress),
  update: (id: number, progress: LocalRecoveryProgress): void =>
    useUploadPreparationStore.getState().update(id, progress),
  complete: (id: number, summary: LocalRecoverySummary): void =>
    useUploadPreparationStore.getState().complete(id, summary),
  fail: (id: number): void => useUploadPreparationStore.getState().fail(id),
  requestStop: (id: number): void => useUploadPreparationStore.getState().requestStop(id),
  dismiss: (): void => useUploadPreparationStore.getState().dismiss(),
};
